import { Component, Host, h, Prop, Element } from '@stencil/core';
import wfCoin from './assets/wf_coin.svg';

/**
 * Component which shows how many loyalty points can be earned for a given
 * amount of money. It also shows a CTA to sign up for the loyalty program.
 *
 * @example
 *
 * <wf-loyalty-points-price-info />
 */
@Component({
    tag: 'wf-loyalty-points-price-info',
    styleUrl: 'wf-loyalty-points-price-info.scss',
    shadow: true,
})
// eslint-disable-next-line require-jsdoc
export class WfLoyaltyPointsPriceInfo {
    /**
     * The points which should be displayed.
     */
    @Prop() points?: number;

    /**
     * Determines if the CTA should be shown or not. This also changes the appearance of the component.
     */
    @Prop() hasCta?: boolean = false;

    /**
     * The link for the CTA. It is only shown if the hasCta prop is true.
     */
    // eslint-disable-next-line @typescript-eslint/no-inferrable-types
    @Prop() ctaLink?: string;

    /**
     * Determines if the component should be rendered as a skeleton.
     */
    @Prop() skeleton?: boolean = false;

    /**
     * Determines if the component is rendered in the checkout.
     */
    @Prop() isCheckout?: boolean = false;

    /**
     * Determines if the user is authenticated.
     */
    @Prop() isAuth?: boolean = false;

    /**
     * Host element of the component.
     */
    @Element() host: HTMLElement;

    /**
     * Generates the text for the point earn information depending on the hasCta prop.
     */
    private pointEarnText() {
        // eslint-disable-next-line no-nested-ternary
        const snippet = this.isCheckout
            ? this.isAuth
                ? 'checkout'
                : 'checkoutNoAuth'
            : this.isAuth
                ? 'productAuth'
                : 'productNoAuth';

        const pointsToEarn = this.points ?? 0

        const snippets = {
            productNoAuth: `Mit <span class="highlight">Weinfreunde+</span> sammelst du mindestens <span class="highlight">${pointsToEarn} Punkte</span> für dieses Produkt`,
            productAuth: `Mit diesem Produkt sammelst du <span class="highlight">${pointsToEarn} Punkte</span>.`,
            checkoutNoAuth: `Mit diesem Einkauf würdest du <span class="highlight">${pointsToEarn} Punkte</span> sammeln.`,
            checkout: `Du sammelst mindestens <span class="highlight">${pointsToEarn} Punkte</span>. Deinen finalen Punktestand (inkl. der Punkte aus Treueaktionen) erhältst du nach Bestellabschluss.`,
        };
        return snippets[snippet];
    }

    /**
     * @inheritdoc
     */
    componentDidLoad() {
        const hostElement = this.host.shadowRoot.host;

        if (!this.skeleton) {
            hostElement.classList.remove('is-skeleton');
        }
        if (!this.hasCta) {
            hostElement.classList.remove('has-cta');
        }
    }

    /**
     * Renders the component.
     *
     * @returns {JSX.Element} - The rendered component.
     */
    render() {
        return (
            <Host
                class={{
                    'loyalty-product-info': true,
                    'has-cta': this.hasCta,
                    'is-skeleton': this.skeleton,
                }}
            >
                <div class="loyalty-point-earn">
                    <img src={wfCoin} alt="Weinfreunde+ Coin" />
                    <div
                        class="loyalty-point-earn-info"
                        innerHTML={this.pointEarnText()}
                    ></div>
                </div>
                {this.hasCta && (
                    <div class="loyalty-cta">
                        <wf-loyalty-cta text='Anmelden' link={this.ctaLink} />
                    </div>
                )}
            </Host>
        );
    }
}
